import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { fbt, useLocaleContext } from 'fbtee'
import type { SearchEvent, SearchSet } from '@shared/searchIndex'
import { PageLayout } from '../../components/PageLayout'
import { NotFoundState } from '../../components/NotFoundState'
import { Section } from '../../components/Section'
import { EmptyState } from '../../components/EmptyState'
import { ArtistBio } from '../../components/ArtistBio'
import { EventResultRow } from '../../components/GlobalSearch/SearchResultRows'
import { useAuth } from '../../hooks/useAuth'
import { useArtistsByIds } from '../../hooks/useArtistsByIds'
import { useArtistDetails } from '../../hooks/useArtistDetails'
import { useArtistInterests } from '../../hooks/useArtistInterests'
import { useArtistAppearances } from '../../hooks/useArtistAppearances'
import { useUserEventInterests } from '../../hooks/useUserEventInterests'
import { useHomeRegion } from '../../hooks/useHomeRegion'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { usePageLoadTrace } from '../../lib/perf'
import { track } from '../../lib/analytics'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { interestPrompt } from '../../lib/guestPrompts'
import { isAway } from '../../lib/searchRanking'
import { placeholders } from '../../lib/skeletonUtils'
import { scheduleSetPath } from '../../lib/utils'
import { ArtistHero } from './ArtistHero'
import { ArtistAppearanceList } from './ArtistAppearanceList'
import { ArtistRelations } from './ArtistRelations'

const upcomingTitle = () => fbt('Upcoming', 'Section heading on the artist page for events the artist plays next')

export default function ArtistPage() {
  const { artistId } = useParams<{ artistId: string }>()
  const { locale } = useLocaleContext()
  const { signedInUid } = useAuth()
  const navigate = useTransitionNavigate()
  const openEvent = useOpenEvent()
  const { guard } = useGuestGuard()
  const { regionId } = useHomeRegion()

  const ids = useMemo(() => (artistId ? [artistId] : []), [artistId])
  const { artists, loading: artistLoading } = useArtistsByIds(ids)
  const { details, loading: detailsLoading } = useArtistDetails(artistId ?? null)
  const { artist: indexed, upcoming, past, members, collabs, loading: indexLoading } = useArtistAppearances(artistId)
  const { interestedArtistIds, toggleArtistInterest } = useArtistInterests(signedInUid)
  const { eventStatuses } = useUserEventInterests(signedInUid)

  // The search index paints the name and image before the artist doc lands.
  const artist = artistId ? artists[artistId] : undefined
  const name = artist?.name ?? indexed?.name
  const notFound = !artistLoading && !indexLoading && !name

  useDocumentTitle(name ? pageTitle(name) : null)
  usePageLoadTrace('artist_page_to_content', Boolean(name) && !indexLoading)

  if (notFound) {
    return (
      <NotFoundState message={fbt('Artist not found', 'Message on the artist page when the artist id does not exist')} />
    )
  }

  const toggle = guard(() => { if (artistId) void toggleArtistInterest(artistId) }, interestPrompt('artist_interest'))
  const away = (event: SearchEvent) => isAway(event, regionId)
  const selectEvent = (event: SearchEvent) => openEvent(event, eventStatuses.get(event.id))
  const selectSet = (event: SearchEvent, set: SearchSet) => navigate(scheduleSetPath(event.id, set.id))
  const selectArtist = (id: string) => {
    track('artist_detail_opened', { artist_id: id, entry_point: 'artist' })
    navigate(`/artists/${id}`)
  }

  return (
    <PageLayout back className="space-y-6">
      <ArtistHero
        loading={!name}
        name={name}
        imageUrl={artist?.imageUrl ?? indexed?.imageUrl}
        imageFocal={artist}
        country={details?.country}
        genres={artist?.genres}
        interested={artistId ? interestedArtistIds.has(artistId) : false}
        onToggle={toggle}
      />
      <ArtistRelations members={members} collabs={collabs} onSelect={selectArtist} />
      <ArtistBio details={details} loading={detailsLoading} />
      {indexLoading ? (
        <Section title={upcomingTitle()} loading>
          {placeholders(3).map((_, i) => <EventResultRow key={i} loading />)}
        </Section>
      ) : upcoming.length === 0 && past.length === 0 ? (
        <EmptyState>
          {fbt('No events with this artist yet.', 'Empty state on the artist page when the artist is on no published lineup')}
        </EmptyState>
      ) : (
        <>
          {upcoming.length > 0 && (
            <Section title={upcomingTitle()}>
              <ArtistAppearanceList
                appearances={upcoming}
                past={false}
                isAway={away}
                locale={locale}
                onSelectEvent={selectEvent}
                onSelectSet={selectSet}
              />
            </Section>
          )}
          {past.length > 0 && (
            <Section title={fbt('Past', 'Section heading on the artist page for events the artist already played')}>
              <ArtistAppearanceList
                appearances={past}
                past
                isAway={away}
                locale={locale}
                onSelectEvent={selectEvent}
                onSelectSet={selectSet}
              />
            </Section>
          )}
        </>
      )}
    </PageLayout>
  )
}
